// Session credential store for the tenant-app bearer token + signed-in user.
//
// The tenant API authenticates each /api/* call with `Authorization: Bearer
// <token>` (issued by POST /api/auth/login). Per ARCHITECTURE §1 the tenant app
// uses session auth; we keep the token only for the browser session in
// sessionStorage (cleared on logout or any 401).

import type { AuthUser } from "./types";

const TOKEN_KEY = "ac_app_token";
const USER_KEY = "ac_app_user";

export interface Session {
  token: string;
  user: AuthUser;
}

let cached: Session | null = null;

function read(): Session | null {
  if (cached) return cached;
  try {
    const token = sessionStorage.getItem(TOKEN_KEY);
    const rawUser = sessionStorage.getItem(USER_KEY);
    if (!token || !rawUser) return null;
    const user = JSON.parse(rawUser) as AuthUser;
    cached = { token, user };
    return cached;
  } catch {
    return cached;
  }
}

export function getSession(): Session | null {
  return read();
}

export function getToken(): string | null {
  return read()?.token ?? null;
}

export function getCurrentUser(): AuthUser | null {
  return read()?.user ?? null;
}

export function setSession(session: Session): void {
  cached = session;
  try {
    sessionStorage.setItem(TOKEN_KEY, session.token);
    sessionStorage.setItem(USER_KEY, JSON.stringify(session.user));
  } catch {
    /* sessionStorage may be unavailable; in-memory cache still works */
  }
}

export function clearSession(): void {
  cached = null;
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
}

export function isAuthenticated(): boolean {
  return !!getToken();
}
